// src/pages/buyer/SwatchSubscriptionPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { swatchSubscriptionApi } from '@/api/services'
import { Package, Check, Loader2, ThumbsUp, ThumbsDown, QrCode, Truck } from 'lucide-react'
import toast from 'react-hot-toast'

const planColors = {
  basic:      'border-blue-300 bg-blue-50',
  premium:    'border-purple-300 bg-purple-50',
  enterprise: 'border-amber-300 bg-amber-50',
}

const statusColors = {
  preparing: 'bg-yellow-100 text-yellow-700',
  shipped:   'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
}

const FABRIC_TYPES = ['Cotton', 'Silk', 'Linen', 'Wool', 'Polyester', 'Chiffon', 'Georgette', 'Khadi', 'Denim', 'Velvet']
const COLORS = ['White', 'Black', 'Red', 'Blue', 'Green', 'Yellow', 'Pink', 'Purple', 'Brown', 'Grey']

export default function SwatchSubscriptionPage() {
  const qc = useQueryClient()
  const [prefs, setPrefs] = useState({ fabric_types: [], colors: [], use_case: '' })
  const [selectedPlan, setSelectedPlan] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['swatch-subscription'],
    queryFn: () => swatchSubscriptionApi.index().then(r => r.data),
  })

  const { data: boxesData } = useQuery({
    queryKey: ['swatch-boxes'],
    queryFn: () => swatchSubscriptionApi.boxes().then(r => r.data),
    enabled: !!data?.data?.subscription,
  })

  const subscribe = useMutation({
    mutationFn: (d) => swatchSubscriptionApi.subscribe(d),
    onSuccess: (res) => {
      toast.success(res.data.message)
      qc.invalidateQueries({ queryKey: ['swatch-subscription'] })
      setSelectedPlan(null)
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to subscribe'),
  })

  const cancel = useMutation({
    mutationFn: () => swatchSubscriptionApi.cancel(),
    onSuccess: () => {
      toast.success('Subscription cancelled')
      qc.invalidateQueries({ queryKey: ['swatch-subscription'] })
    },
  })

  if (isLoading) return <div className="text-center py-12 text-gray-400">Loading...</div>

  const info = data?.data || {}
  const plans = info.plans || []
  const subscription = info.subscription
  const boxes = boxesData?.data || []

  function handleSubscribe(e) {
    e.preventDefault()
    subscribe.mutate({ plan: selectedPlan, preferences: prefs })
  }

  function togglePref(type, value) {
    setPrefs(p => ({
      ...p,
      [type]: p[type].includes(value) ? p[type].filter(v => v !== value) : [...p[type], value],
    }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Package className="w-6 h-6 text-pink-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Swatch Subscription Box</h1>
          <p className="text-xs text-gray-500">
            {subscription
              ? `Active plan: ${subscription.plan} — ${subscription.swatches_per_month} swatches/month`
              : 'Get curated fabric swatches delivered to your doorstep every month'}
          </p>
        </div>
      </div>

      {/* Active subscription */}
      {subscription ? (
        <div className="space-y-6">
          {/* Current plan card */}
          <div className={`rounded-xl border-2 p-6 ${planColors[subscription.plan]}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900 capitalize">{subscription.plan} Plan</h2>
                <p className="text-sm text-gray-600">
                  {subscription.swatches_per_month} swatches/month — ₹{subscription.price_per_month}/mo
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Next dispatch: {subscription.next_dispatch_date || 'TBD'}
                </p>
              </div>
              <button onClick={() => cancel.mutate()} disabled={cancel.isPending}
                className="px-4 py-2 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition">
                Cancel Subscription
              </button>
            </div>

            {/* Preferences */}
            {subscription.preferences && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-1">Your preferences:</p>
                <div className="flex flex-wrap gap-1">
                  {(subscription.preferences.fabric_types || []).map(t => (
                    <span key={t} className="px-2 py-0.5 bg-white/70 rounded-full text-xs text-gray-700">{t}</span>
                  ))}
                  {(subscription.preferences.colors || []).map(c => (
                    <span key={c} className="px-2 py-0.5 bg-white/70 rounded-full text-xs text-gray-700">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Box history */}
          <div>
            <h2 className="font-bold text-gray-900 mb-3">Swatch Box History</h2>
            {boxes.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No boxes dispatched yet. Your first box is being prepared!</p>
            ) : (
              <div className="space-y-4">
                {boxes.map(box => (
                  <div key={box.id} className="bg-white rounded-xl border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          Box #{box.id}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[box.status]}`}>
                          {box.status}
                        </span>
                      </div>
                      {box.qr_code && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <QrCode className="w-3.5 h-3.5" />
                          QR: {box.qr_code}
                        </div>
                      )}
                    </div>
                    {box.items?.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {box.items.map(item => (
                          <div key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{item.product?.name}</p>
                              <p className="text-[10px] text-gray-500">{item.swatch_size}" swatch</p>
                            </div>
                            {item.liked !== null && (
                              item.liked
                                ? <ThumbsUp className="w-3.5 h-3.5 text-green-500" />
                                : <ThumbsDown className="w-3.5 h-3.5 text-red-400" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Plan selection */
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map(plan => (
              <div key={plan.key}
                className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${
                  selectedPlan === plan.key
                    ? `${planColors[plan.key]} ring-2 ring-offset-2 ring-pink-400`
                    : `${planColors[plan.key]} hover:shadow-md`
                }`}
                onClick={() => setSelectedPlan(plan.key)}>
                <h3 className="font-bold text-lg text-gray-900">{plan.name}</h3>
                <div className="mt-2 mb-3">
                  <span className="text-3xl font-extrabold text-gray-900">₹{plan.price}</span>
                  <span className="text-sm text-gray-500">/month</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{plan.swatches_per_month} swatches/month</p>
                <ul className="space-y-1.5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                      <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Preferences + subscribe */}
          {selectedPlan && (
            <form onSubmit={handleSubscribe} className="bg-white rounded-xl border p-6 space-y-4">
              <h2 className="font-bold text-gray-900">Set Your Preferences</h2>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Fabric Types (select all that apply)</label>
                <div className="flex flex-wrap gap-2">
                  {FABRIC_TYPES.map(t => (
                    <button key={t} type="button" onClick={() => togglePref('fabric_types', t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                        prefs.fabric_types.includes(t)
                          ? 'bg-pink-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>{t}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Preferred Colors</label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => togglePref('colors', c)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                        prefs.colors.includes(c)
                          ? 'bg-pink-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>{c}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Use Case</label>
                <input type="text" value={prefs.use_case}
                  onChange={e => setPrefs(p => ({ ...p, use_case: e.target.value }))}
                  placeholder="e.g. Fashion design, Home furnishing, Ethnic wear"
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>

              <button type="submit" disabled={subscribe.isPending}
                className="px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-pink-500 hover:bg-pink-600 transition disabled:opacity-50 flex items-center gap-2">
                {subscribe.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Subscribe to {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  )
}
